import type { ReactNode } from 'react';

/**
 * Table — admin / 分析アプリ向けの table primitive (Sprint BA、docs/16 §10-2 残 component)。
 *
 * 設計方針:
 *   - native `<table>` ベース (SR / browser ショートカット互換性)
 *   - sticky header + zebra striping
 *   - sort は controlled (parent が `sortKey` / `sortDir` を保持)、`onSort(key)` で通知
 *   - row data は `Record<string, ReactNode>` (cell ごとに任意 JSX 可) で columns.key と紐付け
 *   - 空 state は `emptyMessage` で固定 row 1 つ描画 (a11y にやさしい)
 *   - cell に id 等の連想 string が必要なら row['__rowKey'] (オプション) を使う
 */

export interface TableColumn {
  key: string;
  label: ReactNode;
  sortable?: boolean;
  /** th に付与する className (width 指定など). */
  className?: string;
}

export type SortDir = 'asc' | 'desc';

export interface TableRow {
  /** React key 用 (推奨 unique). 無ければ index を使う. */
  __rowKey?: string;
  [k: string]: ReactNode;
}

export interface TableProps {
  columns: TableColumn[];
  rows: TableRow[];
  /** sort 中の column key. */
  sortKey?: string;
  /** sort 方向. */
  sortDir?: SortDir;
  /** sortable header click 時のコールバック. */
  onSort?: (key: string) => void;
  /** rows が空のときの表示文言. default 「データがありません」. */
  emptyMessage?: ReactNode;
  /** rows[0] の値を row header にするか (1 列目に scope="row"). default false. */
  rowHeaderKey?: string;
  className?: string;
  /** table caption (a11y で SR が読み上げる; 視覚的には sr-only). */
  caption?: ReactNode;
}

const TH_BASE =
  'sticky top-0 z-10 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200';
const TD_BASE = 'px-3 py-2 text-sm text-slate-700 border-b border-slate-100';

export function Table({
  columns,
  rows,
  sortKey,
  sortDir,
  onSort,
  emptyMessage = 'データがありません',
  rowHeaderKey,
  className = '',
  caption,
}: TableProps) {
  return (
    <div className={`overflow-auto ${className}`.trim()}>
      <table className="w-full border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const isActive = sortKey === col.key;
              const ariaSort: 'ascending' | 'descending' | 'none' | undefined = col.sortable
                ? isActive
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
                : undefined;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={`${TH_BASE} ${col.className ?? ''}`.trim()}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.key)}
                      className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
                    >
                      {col.label}
                      <span aria-hidden="true" className="text-slate-400">
                        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={`${TD_BASE} text-center text-slate-500`}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={row.__rowKey ?? idx}
                className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}
              >
                {columns.map((col, ci) => {
                  const isRowHeader = rowHeaderKey
                    ? col.key === rowHeaderKey
                    : ci === 0 && Boolean(rowHeaderKey);
                  if (isRowHeader) {
                    return (
                      <th key={col.key} scope="row" className={`${TD_BASE} font-medium`}>
                        {row[col.key]}
                      </th>
                    );
                  }
                  return (
                    <td key={col.key} className={TD_BASE}>
                      {row[col.key]}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
